export const enum SearchApproaches {
    Auto = "auto",
    Text = "text",
    Aggregator = "aggregator",
}

export const enum ModelVersions {
    PRO = "pro",
    LITE = "lite",
}

export const enum TokensUsageModes {
    Standard = "standard",
    Increased = "increased",
    Highest = "highest",
}

export const enum ImproveSearchQueryModes {
    Always = "Always",
    Manual = "Manual",
    Disabled = "Disabled",
}

export const enum PreselectContextFilesModes {
    Enabled = "Enabled",
    Disabled = "Disabled",
}

export const enum UserTypes {
    Beginner = "beginner",
    Expert = "expert",
}

export const enum AssistantTypes {
    BASIC = "basic",
    DOC_ASSISTANT = "doc_assistant",
    INTERNET_SEARCH = "internet_search",
}

export const enum InteractionTypes {
    ANSWER = "answer",
    CLARIFICATION_REQUEST = "clarification_request",
}

export type AskRequestOverrides = {
    temperature?: number
    answer_generation_prompt_template?: string
    cognitive_search_query_generation_prompt_template?: string
    preselect_context_files_mode?: PreselectContextFilesModes
    isChatGPT4?: boolean
    assistant_type?: AssistantTypes
    search_approach: SearchApproaches
    docs_preselect_search_threshold?: number
    model_provider: string
    selected_model?: string
    tokens_usage_mode: TokensUsageModes
    reasoning_enabled?: boolean
    allow_general_knowledge?: boolean
    suggest_followup_questions?: boolean
}

export type AttachedImage = {
    blob_name: string
    mime_type: string
    original_name?: string | null
    size?: number | null
    sas_url?: string | null
}

export type AttachedFile = {
    blob_name: string
    mime_type: string
    original_name?: string | null
    size?: number | null
    /** Truncated extracted text; persisted for LLM replay (workflow + chat). */
    extracted_text?: string | null
    sas_url?: string | null
}

export type VisualEvidence = {
    evidence_id: string
    source_kind: "document_figure" | "document_page" | "chat_attachment"
    source_id: string
    source_revision?: string | null
    content_hash?: string | null
    file_id?: string | null
    chat_id?: string | null
    blob_name?: string | null
    original_name?: string | null
    page_number?: number | null
    region: {
        x1: number
        y1: number
        x2: number
        y2: number
        label?: string | null
        confidence?: number | null
    }
    role: "object" | "value" | "dimension" | "legend" | "context"
    status: "confirmed" | "approximate" | "unreadable" | "ambiguous" | "unavailable"
    label?: string | null
    claim_id?: string | null
    parent_evidence_id?: string | null
}

export type VisualClaim = {
    claim_id: string
    evidence_ids: string[]
    text: string
}

export type History = {
    _id?: string
    user: string
    bot: {
        answer: string
        /** Chain-of-thought / extended thinking from reasoning models (o1, o3, etc.) */
        reasoning?: string | null
        data_points?: string[]
        thoughts?: string
        context_files_id?: string[]
        real_time_connectors?: string[]
        was_interrupted: boolean
        tokens_used?: {
            prompt_tokens?: number
            completion_tokens?: number
            total_tokens?: number
        }
        settings?: {
            search_approach?: SearchApproaches
            overrides?: AskRequestOverrides
            context_files_id?: string[]
            tokens_used?: number
        }
        /** Type of agent interaction — "answer" (default) or "clarification_request" */
        interaction_type?: InteractionTypes
        /** Quick-reply options when interaction_type is clarification_request */
        clarification_options?: string[]
        /** Selection mode for clarification — "single" (radio) or "multi" (checkboxes) */
        clarification_selection_mode?: "single" | "multi" | null
        visual_evidence?: VisualEvidence[] | null
        visual_claims?: VisualClaim[] | null
        connector_connect?: ConnectorConnectPrompt | null
    }
    created_time_utc: any
    attached_images?: AttachedImage[] | null
    attached_files?: AttachedFile[] | null
    id?: string
    hide_on_ui?: boolean
    hide_question_on_ui?: boolean
}

export type ConnectorConnectPrompt = {
    service: string
    service_name: string
    request_id?: string
    expires_at?: string
    reconnect?: boolean
    resume_after_connect?: boolean
}

export type AskResponse = {
    cited_figures: any
    visual_evidence?: VisualEvidence[] | null
    visual_claims?: VisualClaim[] | null
    was_interrupted: boolean
    title: string
    answer: string
    thoughts: any
    data_points: string[]
    is_pin: boolean
    history: History[]
    detail?: string
    tokens_used: {
        prompt_tokens: number
        completion_tokens: number
        total_tokens: number
    }
    _id?: string
    chat_id?: string
    execution_time?: number
    settings: any
    project_id?: string
    is_from_internal_knowledge?: boolean
    created_by_user_id?: string | null
    created_by_email?: string | null
    is_owner?: boolean | null
    connector_connect?: ConnectorConnectPrompt | null
}

export type ChatRequest = {
    user_question: string
    overrides?: AskRequestOverrides
    context_files_id: string[]
    context_folders?: string[]
    chat_id?: string
    should_be_pinned?: boolean
    user_type: UserTypes
    project_id?: string
    images?: string[]
    image_names?: string[]
    existing_images?: AttachedImage[]
    files?: string[]
    file_names?: string[]
    existing_files?: AttachedFile[]
    timezone?: string
}
